import type { CSSProperties, ReactElement, ReactNode } from 'react';

import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import type { TextProps } from 'components/atoms/Text';
import Text from 'components/atoms/Text';
import type { TooltipProps } from 'components/atoms/Tooltip';
import Tooltip from 'components/atoms/Tooltip';
import useIsOverflowing from '../../../hooks/useIsOverflowing';
import * as Styled from './TextOverflow.styled';

const TextOverflow = ({
  children,
  className = 'w-full',
  textProps,
  tooltipProps,
  style,
  contentDependency,
  'data-test-id': dataTestId,
}: {
  children: ReactNode;
  className?: string;
  tooltipProps?: { content?: ReactNode } & Omit<
    TooltipProps,
    'content' | 'targetOverflow' | 'children'
  >;
  textProps?: TextProps;
  style?: CSSProperties;
  contentDependency?: unknown;
  'data-test-id'?: string;
} & Omit<TooltipProps, 'content' | 'targetOverflow'>) => {
  const { targetRef, isOverflow } = useIsOverflowing(
    'scrollWidth',
    undefined,
    contentDependency,
  );

  return (
    <ConditionalWrapper
      condition={isOverflow}
      wrapper={wrapperChildren => (
        <Tooltip
          content={
            <Styled.OverflowTooltipText>{children}</Styled.OverflowTooltipText>
          }
          targetOverflow={isOverflow}
          {...tooltipProps}
        >
          {wrapperChildren as ReactElement}
        </Tooltip>
      )}
    >
      <Text
        {...textProps}
        ref={targetRef}
        className={className}
        data-test-id={dataTestId}
        style={{
          ...style,
          // Style doesn't apply when using `as` prop somehow so we need to use inline styles here
          overflow: 'hidden',
          padding: 0,
          textAlign: 'left',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          ...style,
        }}
      >
        {children}
      </Text>
    </ConditionalWrapper>
  );
};

export default TextOverflow;
